/**
 * Making a call go immersive — with the honest mechanism for each platform,
 * rather than one mechanism and three workarounds.
 *
 * ==========================================================================
 * THE FACT THE WHOLE FEATURE RESTS ON, verified 2026-08-08, not inherited
 * ==========================================================================
 *
 * **iPhone Safari has no Fullscreen API for arbitrary elements. It never has,
 * and as of iOS 26.5 it still does not.** Three independent primary sources,
 * all checked on the date above:
 *
 *  - MDN browser-compat-data, `api/Element.json` → `Element.requestFullscreen`
 *    → `safari_ios`: `{"version_added": "16.4", "partial_implementation": true,
 *    "notes": ["Only available on iPad, not on iPhone.", …]}`. The prefixed
 *    `webkit` entry from iOS 12 carries the same iPad-only note.
 *  - caniuse `features-json/fullscreen.json` → every `ios_saf` release from 12
 *    through 26.5 is `a #5`, and note 5 reads: "Partial support refers to
 *    supporting only iPad, not iPhone."
 *  - WebKit bug 206854, "Add Fullscreen API to iOS" — still **NEW / open /
 *    unassigned** after six years, most recent comment 2026-06-08. Its
 *    duplicate, bug 212934, was closed WONTFIX on 2026-01-25.
 *
 * You will find forum posts claiming Safari 17.2/17.4 beta shipped it. That
 * was an experimental Feature Flag, never enabled by default; the support
 * tables above post-date those posts by two years and still say iPad-only.
 *
 * So the platform split is real:
 *
 *  | platform             | what a call gets                                |
 *  |----------------------|-------------------------------------------------|
 *  | desktop, Android     | real element fullscreen, entered on the join tap |
 *  | iPad (iOS ≥ 16.4)    | the same, plus a system overlay button we cannot |
 *  |                      | hide and a swipe-down exit we must tolerate      |
 *  | iPhone               | nothing from this API. Web app manifest +        |
 *  |                      | `display: standalone` + Add to Home Screen       |
 *
 * **What we deliberately do NOT do on iPhone.** `<video>.webkitEnterFullscreen()`
 * exists there and technically "works". It hands the media element to the
 * native iOS player: no control bar, no other participants, no self-view, no
 * recording indicator — the entire call UI is replaced by a scrubber for one
 * person's stream. It is not a degraded version of this feature, it is a
 * different and worse product, so it is not used.
 *
 * ==========================================================================
 * WHY THE ENTRY POINT IS A PLAIN FUNCTION AND NOT AN EFFECT
 * ==========================================================================
 *
 * `requestFullscreen()` needs **transient activation**: it must be reached
 * from the synchronous call stack of a user gesture. The join handler in
 * `routes/call.tsx` is `async` and awaits `POST /rooms/:code/join` — and the
 * first `await` ENDS the activation. An effect that runs when the call turns
 * live is therefore always too late and is always refused.
 *
 * Hence the shape here: `enterFullscreenForCall()` is called imperatively, at
 * the top of the join handler, before anything is awaited. Everything else —
 * exit, wake lock — is lifecycle and lives in `immersive-call.tsx`.
 *
 * Everything below is FEATURE-detected, never version- or UA-sniffed, with a
 * single deliberate exception documented at `isIosSafari()`. If Apple ever
 * resolves bug 206854, iPhone starts taking the fullscreen path and stops
 * being offered the Home Screen hint, with no change here.
 */

/* -------------------------------------------------------------------------- */
/* Element fullscreen                                                          */
/* -------------------------------------------------------------------------- */

/**
 * The `webkit`-prefixed spelling is still carried because it is the ONLY one
 * older iPads (iOS 12 → 16.3) and Safari desktop before 16.4 ever had. It
 * costs a handful of lines and it is the difference between "immersive on
 * iPad" and "immersive on a recent iPad".
 */
type WebkitDocument = Document & {
  webkitFullscreenEnabled?: boolean
  webkitFullscreenElement?: Element | null
  webkitExitFullscreen?: () => Promise<void> | void
}

type WebkitElement = Element & {
  webkitRequestFullscreen?: () => Promise<void> | void
}

/**
 * Does this browser let a NON-video element fill the screen?
 *
 * `document.fullscreenEnabled` is the correct gate and it is what separates
 * iPad from iPhone at runtime without asking either of them what they are:
 * it is `true` on iPad from 16.4 and `false` on every iPhone at every version.
 * Testing for the mere existence of `Element.prototype.requestFullscreen` is
 * the common mistake — the method can exist while the document forbids its
 * use (an iframe without `allow="fullscreen"`, for one).
 */
export function canFullscreen(): boolean {
  if (typeof document === 'undefined') return false
  const doc = document as WebkitDocument
  return Boolean(doc.fullscreenEnabled ?? doc.webkitFullscreenEnabled)
}

/** Is anything on this page currently filling the screen? */
export function isFullscreen(): boolean {
  if (typeof document === 'undefined') return false
  const doc = document as WebkitDocument
  return Boolean(doc.fullscreenElement ?? doc.webkitFullscreenElement)
}

/**
 * Did WE put the page into fullscreen for a call?
 *
 * This is the whole state machine, and it exists for one reason: the user can
 * leave fullscreen without telling us — Escape on desktop, the un-hideable
 * overlay button or a swipe-down on iPad, the Android back gesture. When that
 * happens we must NOT try to exit again on the way out of the call (harmless
 * but noisy), and we must never re-enter and fight them for it.
 *
 * Module scope rather than React state on purpose. The two halves of this live
 * in different frames — entry is inside a tap handler, exit is inside an
 * effect cleanup that may run after the component owning it has gone — and
 * there is exactly one page, so there is exactly one fullscreen.
 */
let ownsFullscreen = false
let watching = false

function watchForForeignExit(): void {
  if (watching || typeof document === 'undefined') return
  watching = true
  const sync = () => {
    if (!isFullscreen()) ownsFullscreen = false
  }
  document.addEventListener('fullscreenchange', sync)
  document.addEventListener('webkitfullscreenchange', sync)
}

/**
 * Go immersive. **Call this synchronously from a user gesture** — see the
 * transient-activation note in the module header.
 *
 * Returns nothing and throws nothing. Fullscreen is an enhancement: every way
 * it can fail (no support, no activation, a permissions policy, the user
 * saying no) ends with a call that works exactly as it does today, so there is
 * no failure here worth interrupting a join for.
 */
export function enterFullscreenForCall(): void {
  if (!canFullscreen() || isFullscreen()) return

  // Ask whether we still hold activation before spending it. Where
  // `navigator.userActivation` exists (Safari 16.4+, Chrome 72+) this turns a
  // guaranteed rejection — and the console warning that comes with it — into
  // a silent no-op, which matters because the one case that lands here without
  // a gesture is a legitimate one: reloading the page back into a live call.
  const activation = (navigator as Navigator & { userActivation?: { isActive: boolean } })
    .userActivation
  if (activation && !activation.isActive) return

  watchForForeignExit()

  // The ROOT element, not the call surface. `#root` is already exactly the
  // viewport (`height: 100dvh` in styles.css) so fullscreening it would look
  // identical — but the root also carries the `dark` class the pre-paint
  // script in index.html sets, and in several engines a fullscreen element is
  // lifted into its own top layer where an ancestor's class no longer applies
  // to anything outside it. Taking the whole document sidesteps the question.
  const target = document.documentElement as WebkitElement

  let request: Promise<void> | void
  try {
    request = target.requestFullscreen
      ? // `navigationUI: 'hide'` is advisory and ignored where unsupported;
        // it asks Chrome/Firefox not to keep a navigation strip on screen.
        target.requestFullscreen({ navigationUI: 'hide' })
      : target.webkitRequestFullscreen?.()
  } catch {
    return // the prefixed spelling throws synchronously rather than rejecting
  }

  Promise.resolve(request)
    .then(() => {
      ownsFullscreen = true
    })
    .catch(() => {
      ownsFullscreen = false
    })
}

/**
 * Give the screen back — but only if we took it.
 *
 * Called from every path that leaves a call, including the ones that never
 * reached one: a join that 503s, a refused token, a rejoin. Those matter more
 * than the happy path. Fullscreen was requested on the TAP, so a join that
 * then fails would otherwise leave the join door filling the screen with no
 * browser chrome — the exact "wrong state" this function exists to prevent.
 */
export function exitFullscreenIfOwned(): void {
  if (!ownsFullscreen) return
  ownsFullscreen = false
  if (!isFullscreen()) return
  const doc = document as WebkitDocument
  try {
    void Promise.resolve(doc.exitFullscreen ? doc.exitFullscreen() : doc.webkitExitFullscreen?.())
      .catch(() => {})
  } catch {
    /* already gone */
  }
}

/* -------------------------------------------------------------------------- */
/* Installed / standalone detection                                            */
/* -------------------------------------------------------------------------- */

/**
 * Is the app running as an installed, chrome-free app rather than in a browser
 * tab?
 *
 * This is a three-way OR and every term is load-bearing. The obvious one-liner
 * — `matchMedia('(display-mode: standalone)')` — is WRONG on the only platform
 * that needs this check:
 *
 *  - **WebKit bug 264218**: in an installed iOS web app whose manifest says
 *    `display: standalone`, `(display-mode: standalone)` reports **false** and
 *    `(display-mode: fullscreen)` reports **true**. Inverted. So the fullscreen
 *    term is not a belt-and-braces extra, it is the term that actually fires
 *    on iPhone.
 *  - `navigator.standalone` is Apple-proprietary and is not in MDN's compat
 *    data at all, so we cannot confirm it still exists on current iOS. It is
 *    one input to the OR and never the sole check.
 *  - Inside Safari itself `(display-mode: browser)` is always true and
 *    `(display-mode: fullscreen)` is never true, so testing for `browser` as a
 *    negative signal would be useless.
 *
 * The `isFullscreen()` guard on the fullscreen term is what keeps this honest
 * on the OTHER platforms: on desktop and Android `(display-mode: fullscreen)`
 * is also true while a page is in *element* fullscreen — which is precisely
 * what this app does during a call — and without the guard every desktop call
 * would report itself as an installed app.
 */
export function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false

  const nav = navigator as Navigator & { standalone?: boolean }
  if (nav.standalone === true) return true

  if (window.matchMedia('(display-mode: standalone)').matches) return true
  if (window.matchMedia('(display-mode: fullscreen)').matches && !isFullscreen()) return true

  return false
}

/**
 * iPhone/iPad running Safari proper — the only place where "tap Share, then
 * Add to Home Screen" is both true and possible.
 *
 * **This is the one UA sniff in the file, and it is here because there is no
 * feature to detect.** iOS has no `beforeinstallprompt`, no `getInstalledRelated
 * Apps`, no API of any kind that says "this browser can install a web app".
 * The alternative to sniffing is showing everyone instructions that are wrong
 * for their browser, which is worse.
 *
 * It is used ONLY to decide whether to show a dismissible hint. Nothing about
 * how a call behaves depends on it. A false negative costs a hint; a false
 * positive costs a wrong sentence — neither breaks a call.
 *
 * Two things it deliberately gets right:
 *  - **iPadOS 13+ lies**, reporting a Mac user agent. `maxTouchPoints > 1` on
 *    a "Mac" is the standard tell, and without it every iPad is missed.
 *  - **Other iOS browsers are excluded.** They are all WebKit underneath, so
 *    no feature test distinguishes them, but Chrome/Firefox/Edge on iOS put
 *    Add to Home Screen somewhere else (or nowhere), and in-app webviews —
 *    Instagram, Facebook, LINE — cannot install at all. The token list cannot
 *    be exhaustive; it covers the ones that are actually common.
 */
export function isIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false
  const ua = navigator.userAgent

  const iphoneOrOldIpad = /iPad|iPhone|iPod/.test(ua)
  const modernIpad = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1
  if (!iphoneOrOldIpad && !modernIpad) return false

  // Other browsers wearing WebKit, and the common in-app webviews.
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA\//.test(ua)) return false
  if (/FBAN|FBAV|Instagram|Line\/|MicroMessenger|Snapchat|Twitter/.test(ua)) return false

  // Safari proper always carries both. A bare WKWebView carries neither.
  return /Safari/.test(ua) && /Version\//.test(ua)
}

/**
 * Is Add to Home Screen the ONLY route this device has to a chrome-free call?
 *
 * The three predicates above are each about one fact; this is the question the
 * UI actually asks, and it lives here — beside the facts — rather than being
 * re-derived at every place that wants to teach the install. It was re-derived
 * in exactly one place before, and that place turned out to be a screen almost
 * nobody reaches (see the mount-site note in `immersive-call.tsx`); a screen
 * that copies this app should be able to lift one call, not three.
 *
 * Order is meaning, not micro-optimisation:
 *
 *  1. `canFullscreen()` — if there is a real immersive route, there is nothing
 *     to teach. This is the term that keeps iPad, Android and desktop out, and
 *     it is the term that will retire this whole feature by itself if WebKit
 *     ever resolves bug 206854 for iPhone.
 *  2. `isIosSafari()` — the instructions name a Share button and a menu item
 *     that only exist here. Wrong instructions are worse than none.
 *  3. `isStandaloneDisplay()` — already installed; the advice is done.
 *
 * Read once at mount by its callers. Every input is fixed for the life of the
 * page, and nothing here should be able to make a banner appear mid-tap.
 */
export function installIsOnlyRouteToImmersive(): boolean {
  return !canFullscreen() && isIosSafari() && !isStandaloneDisplay()
}

/* -------------------------------------------------------------------------- */
/* Wake lock                                                                   */
/* -------------------------------------------------------------------------- */

type WakeLockSentinel = {
  released: boolean
  release: () => Promise<void>
  // An EventTarget in the spec. Optional here because the only thing we do
  // with it is opportunistic, and a shimmed sentinel that lacks it still works
  // through the `released` check below.
  addEventListener?: (type: 'release', listener: () => void) => void
}
type WakeLockNavigator = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinel> }
}

/**
 * Keep the screen awake for the duration of a call.
 *
 * A screen that dims and sleeps thirty seconds into a call is the single most
 * obvious tell that something is a web page and not an app — and on a phone it
 * also kills the camera preview the other side is looking at.
 *
 * Two behaviours are not optional and are the reason this is a small state
 * machine rather than one `await`:
 *
 *  1. **The lock is released automatically whenever the document is hidden**,
 *     and it is NOT restored when you come back. Switching apps to read a
 *     message therefore silently ends the lock for the rest of the call unless
 *     it is re-acquired on `visibilitychange`. This is the bug everyone ships.
 *  2. **`request()` rejects for perfectly ordinary reasons** — the document is
 *     hidden, the battery is low, a Permissions-Policy forbids it — and on
 *     **iOS 16.4 to 18.3 it rejects specifically inside installed Home Screen
 *     web apps** (WebKit bug 254545), which is exactly the mode this feature
 *     pushes iPhone users into. It only became reliable there in 18.4. So the
 *     failure is expected, must be silent, and must be detected at RUNTIME:
 *     version-sniffing that range would be wrong the moment a device updates.
 *
 * Unlike fullscreen this needs no user gesture, so it can safely live in an
 * effect.
 *
 * Returns a handle rather than using module state: a wake lock is scoped to a
 * call, and the caller's effect cleanup is the thing that knows when that ends.
 */
export function createWakeLock(): { start: () => void; stop: () => void } {
  const nav = typeof navigator === 'undefined' ? null : (navigator as WakeLockNavigator)

  let wanted = false
  let sentinel: WakeLockSentinel | null = null

  const acquire = () => {
    if (!wanted || !nav?.wakeLock) return
    // A sentinel we still hold is only useful if it has not already been
    // released underneath us. Testing `sentinel` alone — the obvious version —
    // is precisely the bug in (1) above: after the first trip to the home
    // screen we would hold a dead sentinel forever and never re-request.
    if (sentinel && !sentinel.released) return
    sentinel = null
    // Requesting while hidden is a guaranteed rejection; `visibilitychange`
    // will bring us back.
    if (document.visibilityState !== 'visible') return
    nav.wakeLock
      .request('screen')
      .then((lock) => {
        // The call may have ended during the await. Do not strand a lock that
        // nothing will ever release.
        if (!wanted) {
          void lock.release().catch(() => {})
          return
        }
        sentinel = lock
        // The platform's own release — hiding the document, a low battery —
        // arrives as this event and as nothing else. Bound to `lock` by
        // identity so a late event from a superseded sentinel cannot drop the
        // live one.
        lock.addEventListener?.('release', () => {
          if (sentinel === lock) sentinel = null
        })
      })
      .catch(() => {
        // Unsupported, hidden, low battery, or the iOS 16.4–18.3 standalone
        // gap above. Nothing to tell the user: the call is unaffected, the
        // screen just behaves as the OS default.
      })
  }

  const onVisibility = () => {
    if (document.visibilityState === 'visible') acquire()
  }

  return {
    start() {
      if (wanted) return
      wanted = true
      document.addEventListener('visibilitychange', onVisibility)
      acquire()
    },
    stop() {
      if (!wanted) return
      wanted = false
      document.removeEventListener('visibilitychange', onVisibility)
      const held = sentinel
      sentinel = null
      if (held && !held.released) void held.release().catch(() => {})
    },
  }
}
